import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Barbell, ArrowRight, Spinner, GraduationCap, Users, Megaphone, Student } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "parent", label: "Parent", icon: Users },
  { value: "athlete", label: "Athlete", icon: Student },
  { value: "teacher", label: "Teacher", icon: GraduationCap },
  { value: "coach", label: "Coach", icon: Megaphone },
];

export default function RegisterInvite() {
  const { token } = useParams(); // token === school_id
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [school, setSchool] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | invalid
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "parent", sport: "", child_name: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/school/${token}`)
      .then((r) => { setSchool(r.data); setStatus("ok"); })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/auth/register-public", {
        school_id: token,
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        sport: form.role === "athlete" || form.role === "coach" ? form.sport : null,
        child_name: form.role === "parent" ? form.child_name : null,
      });
      loginWithToken(r.data.token, r.data.user);
      toast.success("Account created — awaiting approval");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not register");
    } finally { setSubmitting(false); }
  };

  const accent = school?.primary_color || "#FF3B30";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          {school?.logo_url ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${school.logo_url}`} alt="" className="h-8 w-8 object-contain" />
          ) : (
            <Barbell size={28} weight="fill" color={accent} />
          )}
          <span className="font-head text-2xl font-extrabold uppercase tracking-tight">
            {school?.name || <>Athlete<span style={{ color: accent }}>Hub</span></>}
          </span>
        </Link>

        {status === "loading" && <div className="text-center text-[#737373]" data-testid="invite-loading">Checking your link…</div>}

        {status === "invalid" && (
          <div className="bg-[#141414] border border-white/10 rounded-sm p-8 text-center" data-testid="invite-invalid">
            <h1 className="font-head text-3xl font-black uppercase tracking-tighter mb-3">Invalid link</h1>
            <p className="text-[#A3A3A3] mb-6">This registration link is not valid. Ask your school's Athletic Director for the correct link.</p>
            <Link to="/login" className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-widest px-6 py-3 rounded-sm" style={{ background: accent }}>Go to sign in</Link>
          </div>
        )}

        {status === "ok" && (
          <div className="bg-[#141414] border border-white/10 rounded-sm p-8" data-testid="register-form">
            <h1 className="font-head text-3xl md:text-4xl font-black uppercase tracking-tighter leading-none mb-2">Join {school.name}</h1>
            <p className="text-[#A3A3A3] mb-6">Create your account. Your Athletic Director will approve it before you get access.</p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">I am a</label>
                <div className="grid grid-cols-2 gap-2" data-testid="role-select">
                  {ROLE_OPTIONS.map((r) => {
                    const Icon = r.icon;
                    const active = form.role === r.value;
                    return (
                      <button type="button" key={r.value} onClick={() => setForm({ ...form, role: r.value })} data-testid={`register-role-${r.value}`}
                        className="flex items-center gap-2 py-3 px-3 rounded-sm border text-sm font-semibold uppercase tracking-wider transition-colors"
                        style={active ? { background: accent, borderColor: accent, color: "#fff" } : { borderColor: "rgba(255,255,255,0.15)", color: "#A3A3A3", background: "#0A0A0A" }}>
                        <Icon size={16} weight="bold" /> {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Your Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input"
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-white/50 rounded-sm p-4 outline-none transition-colors" placeholder="Jane Doe" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Email</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input"
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-white/50 rounded-sm p-4 outline-none transition-colors" placeholder="you@example.com" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Password</label>
                <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input"
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-white/50 rounded-sm p-4 outline-none transition-colors" placeholder="••••••••" />
              </div>

              {(form.role === "athlete" || form.role === "coach") && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Sport</label>
                  <input value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} data-testid="register-sport-input"
                    className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-white/50 rounded-sm p-4 outline-none transition-colors" placeholder="e.g. Basketball" />
                </div>
              )}
              {form.role === "parent" && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Your Child's Name</label>
                  <input value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} data-testid="register-child-input"
                    className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-white/50 rounded-sm p-4 outline-none transition-colors" placeholder="Your athlete's full name" />
                  <p className="text-[11px] text-[#737373] mt-1">The Athletic Director will link you to your athlete when approving.</p>
                </div>
              )}

              <button type="submit" disabled={submitting} data-testid="register-submit-button"
                className="w-full flex items-center justify-center gap-2 disabled:opacity-60 text-white font-bold uppercase tracking-widest px-8 py-4 rounded-sm transition-opacity hover:opacity-90"
                style={{ background: accent }}>
                {submitting ? <Spinner size={20} className="animate-spin" /> : <>Create account <ArrowRight size={18} weight="bold" /></>}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
